import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { FeedbackFormPayload } from '@lib/interfaces';
import { Observable, catchError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class FeedbackService {
    private readonly http = inject(HttpClient);
    private readonly apiBaseUrl = environment.API_URL;

    createFeedbackForm(courseId: string, payload: Omit<FeedbackFormPayload, 'courseId'>): Observable<any> {
        return this.http.post(`${this.apiBaseUrl}/feedback/${courseId}`, payload);
    }

    updateFeedbackForm(courseId: string, payload: Omit<FeedbackFormPayload, 'courseId'>): Observable<any> {
        return this.http.put(`${this.apiBaseUrl}/feedback/${courseId}`, payload);
    }

    getAverageRatings(): Observable<any> {
        // Simple API call without any transformation or error handling
        return this.http.get(`${this.apiBaseUrl}/feedback/average-ratings`);
    }

    getCourseFeedback(
        self: boolean = false,
        live: boolean = true,
        isParent: boolean = false,
        page: number = 0,
        pageSize: number = 10,
        filter?: any,
    ): Observable<any> {
        let params = new HttpParams()
            .set('self', self)
            .set('live', live)
            .set('parent', isParent)
            .set('pageNumber', page)
            .set('pageSize', pageSize);
        if (filter) {
            if (filter.courseRatingMin !== undefined) {
                params = params.set('minRating', filter.courseRatingMin);
            }
            if (filter.courseRatingMax !== undefined) {
                params = params.set('maxRating', filter.courseRatingMax);
            }
            if (filter.reviewCountMin !== undefined) {
                params = params.set('minReviews', filter.reviewCountMin);
            }
            if (filter.reviewCountMax !== undefined) {
                params = params.set('maxReviews', filter.reviewCountMax);
            }
            if (filter.courseIds && Array.isArray(filter.courseIds) && filter.courseIds.length > 0) {
                params = params.set('courseIds', filter.courseIds.join(','));
            }
            // Add other filter params as needed
        }

        return this.http.get(`${this.apiBaseUrl}/feedback/reviews`, { params }).pipe(
            catchError((error) => {
                console.error('Error fetching course feedback:', error);
                throw error;
            }),
        );
    }

    deleteFeedback(id: string): Observable<void> {
        return this.http.delete<void>(`${this.apiBaseUrl}/feedback/${id}`);
    }

    getReviewDetails(courseId: string | number, userId: string | number): Observable<any> {
        return this.http.get(`${this.apiBaseUrl}/course/get-rating-by-user-id/${courseId}/${userId}`);
    }

    getFeedbackDetails(courseId: string | number, page: number = 0, size: number = 5): Observable<any> {
        return this.http.get(`${this.apiBaseUrl}/feedback/${courseId}`, {
            params: new HttpParams().set('pageNumber', page).set('pageSize', size),
        });
    }

    exportFeedbacks(courseId: string | number) {
        return this.http.get(`${this.apiBaseUrl}/feedback/export/${courseId}`, {
            responseType: 'blob',
            observe: 'response',
        });
    }
}
